import type { JSX, MouseEvent } from 'react';
import { useState } from 'react';
import { ActionIcon, Collapse, Group, NavLink, Text } from '@mantine/core';
import { type QuickKind, keepOpen, pickQuick, readOpen, readRecent, switchPath } from './navRecent';

export interface QuickItem {
  id: string;
  code: string;
  name: string;
}

/**
 * A sidebar entry with a short list of projects or offers beneath it, so one
 * click switches between them instead of two (the list, then the item). The
 * ones opened last come first; the list folds away with the arrow.
 */
export function NavQuick<T extends QuickItem>({ kind, label, description, listPath, onList, items, candidate, currentId, total, pathname, link }: {
  kind: QuickKind;
  label: string;
  description: string;
  listPath: string;
  /** Whether the list page itself is open. */
  onList: boolean;
  items: readonly T[];
  /** What may fill the list after the recent ones, e.g. projects not archived. */
  candidate: (item: T) => boolean;
  currentId: string | null;
  total: number;
  pathname: string;
  link: (path: string) => { href: string; onClick: (e: MouseEvent) => void };
}): JSX.Element {
  const [open, setOpen] = useState(() => readOpen(kind));
  // Read on every render: the shell notes each item one opens; the current one comes first anyway.
  const shown = pickQuick(items, candidate, readRecent(kind), currentId);
  const more = total - shown.length;
  const toggle = (): void => { keepOpen(kind, !open); setOpen(!open); };
  return (
    <>
      <Group gap={0} wrap="nowrap">
        <NavLink label={label} description={description} active={onList} component="a" {...link(listPath)} style={{ flex: 1, minWidth: 0 }} />
        {shown.length > 0 && (
          <ActionIcon
            variant="subtle" color="gray" size="sm" onClick={toggle} aria-expanded={open}
            aria-label={open ? `${label} in der Seitenleiste einklappen` : `${label} in der Seitenleiste aufklappen`}
            title={open ? 'Liste einklappen' : 'Liste aufklappen'}
          >
            {open ? '▾' : '▸'}
          </ActionIcon>
        )}
      </Group>
      <Collapse in={open && shown.length > 0}>
        {shown.map((item) => (
          <NavLink
            key={item.id} noWrap py={3} pl={22} active={item.id === currentId} title={`${item.code} ${item.name}`}
            leftSection={<Text span size="xs" ff="monospace" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{item.code}</Text>}
            label={<Text span size="sm">{item.name}</Text>}
            component="a" {...link(switchPath(kind, pathname, item.id))}
          />
        ))}
        {more > 0 && (
          <NavLink py={2} pl={22} label={<Text span size="xs" c="dimmed">alle {total} anzeigen …</Text>} component="a" {...link(listPath)} />
        )}
      </Collapse>
    </>
  );
}
